import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/TranslationContext';

const STRAY_HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/wen9hng6_Stray%20Pic%201.jpeg';
const LOGO_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/m4tj8pbh_1Smartpaw%20Post%20-%2034.PNG';

const HeroSection = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useTranslation();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-[#FAFAF7] overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={STRAY_HERO_IMAGE}
          alt="Stray dog on Georgian street"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
      </div>

      {/* Logo in top left */}
      <div className="absolute top-8 left-8 z-20">
        <img
          src={LOGO_IMAGE}
          alt="SmartPaw Care"
          className="h-24 w-auto md:h-32 drop-shadow-2xl"
        />
      </div>

      {/* Language & Social Media - Top Right */}
      <div className="absolute top-8 right-8 z-20 flex items-center gap-4">
        {/* Language Switcher */}
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-4 py-2">
          <button 
            onClick={() => setLanguage('en')}
            className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'en' ? 'text-white' : 'text-white/60 hover:text-[#F4A460]'}`}
            data-testid="lang-en"
          >
            EN
          </button>
          <span className="text-white/40">|</span>
          <button 
            onClick={() => setLanguage('ge')}
            className={`font-['Manrope'] font-semibold text-sm transition-colors ${language === 'ge' ? 'text-white' : 'text-white/60 hover:text-[#F4A460]'}`}
            data-testid="lang-ge"
          >
            GE
          </button>
        </div>

        {/* Social Media Icons */}
        <div className="flex items-center gap-3">
          <a 
            href="https://www.facebook.com/yoursmartpaw" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-[#F4A460] transition-all duration-200"
            data-testid="social-facebook"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a 
            href="https://www.instagram.com/smartpaw__/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-[#F4A460] transition-all duration-200"
            data-testid="social-instagram"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a 
            href="https://www.tiktok.com/@smartpaw__" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-10 h-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-[#F4A460] transition-all duration-200"
            data-testid="social-tiktok"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            >
              {/* Main Headline */}
              <h1 className="font-['Outfit'] font-bold text-5xl md:text-7xl lg:text-8xl text-white tracking-tight leading-tight mb-8">
                {t('hero.title1')}
                <br />
                <span className="text-[#F4A460]">{t('hero.title2')}</span>
              </h1>

              {/* Slogan */}
              <p className="font-['Playfair_Display'] text-2xl md:text-4xl text-white/90 mb-6 italic">
                {t('hero.slogan')}
              </p>

              {/* Supporting Text */}
              <p className="font-['Manrope'] text-lg md:text-xl text-white/80 mb-12 leading-relaxed max-w-2xl">
                {t('hero.description')}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/become-sponsor')}
                  data-testid="hero-become-sponsor-cta"
                  className="px-8 py-4 bg-[#F4A460] text-white rounded-full font-['Manrope'] font-semibold text-lg hover:bg-[#E89450] transform hover:scale-105 transition-all duration-200 shadow-[0_8px_30px_rgba(244,164,96,0.4)]"
                >
                  {t('hero.becomeSponsor')}
                </button>
                <button
                  onClick={() => navigate('/become-host')}
                  data-testid="hero-become-host-cta"
                  className="px-8 py-4 bg-white/10 backdrop-blur-xl text-white border-2 border-white/30 rounded-full font-['Manrope'] font-semibold text-lg hover:bg-white/20 transform hover:scale-105 transition-all duration-200"
                >
                  {t('hero.becomeHost')}
                </button>
              </div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="mt-16 flex flex-wrap gap-8 text-white/70 text-sm font-['Manrope']"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('hero.trustIndicators.feeding')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('hero.trustIndicators.waste')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#F4A460]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{t('hero.trustIndicators.health')}</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Stats Box */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
              className="hidden lg:block"
            >
              <div className="bg-white/10 backdrop-blur-xl border-2 border-white/20 rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.3)]">
                <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-8 text-center">
                  {t('hero.impact.title')}
                </h3>
                
                <div className="space-y-6">
                  <div className="text-center pb-6 border-b border-white/20">
                    <div className="text-5xl font-bold text-[#F4A460] font-['Outfit'] mb-2">
                      $1,750
                    </div>
                    <div className="text-white/80 font-['Manrope'] text-sm uppercase tracking-wider">
                      {t('hero.impact.donations')}
                    </div>
                  </div>

                  <div className="text-center pb-6 border-b border-white/20">
                    <div className="text-5xl font-bold text-[#F4A460] font-['Outfit'] mb-2">
                      8
                    </div>
                    <div className="text-white/80 font-['Manrope'] text-sm uppercase tracking-wider">
                      {t('hero.impact.feeders')}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-5xl font-bold text-[#F4A460] font-['Outfit'] mb-2">
                      34
                    </div>
                    <div className="text-white/80 font-['Manrope'] text-sm uppercase tracking-wider">
                      {t('hero.impact.strays')}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <svg className="w-6 h-6 text-white/60" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </motion.div>
    </div>
  );
};

export default HeroSection;
